import { BlockStack, Card, IndexTable } from '@shopify/polaris';
import type { IndexTableHeading } from '@shopify/polaris/build/ts/src/components/IndexTable';
import type { NonEmptyArray } from '@shopify/polaris/build/ts/src/types';
import { memo, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IListLocation } from '@/types/components/location';
import EmptyStatePage from '../../../../components/EmptyStatePage/EmptyState';

import BoldSmallText from '@/components/BoldSmallText';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FilterTableLocations from './FilterTableLocations';
import RowTableLocations from './RowTableLocations';
import TableDelivery from './TableDelivery';
import { PATH } from '@/constants/path';
import { SETTING_TYPE, LocationViewChoices, TypeDelete } from '@/constants/enum';
import { isEmpty, openErrorToast, openToast } from '@/helpers';
import { useDeleteDeliveryZoneMutation, useDeleteLocationMutation, useDuplicateDeliveryZoneMutation } from '@/redux/query';
import { config } from '@/config';
import { useDispatch } from 'react-redux';
import { handleLocationTab } from '@/redux/slice/tab.slice';
import ModalDeleteLocations from '../Modal/ModalDeleteLocations';
import ModalDuplicateLocation from '../Modal/ModalDuplicateLocation';
import ModalUpgradePlan from '../Modal/ModalUpgradePlan';
import { ModalSortLocation } from '../Modal/ModalSortLocation';

export type TTableLocation = {
  listLocations: IListLocation[];
  selectedView: number;
  refetchLocation: () => void;
};

type TEmptyState = {
  [key: number]: {
    heading: string;
    textEmptyState: string;
    contentAction: string;
    onAction: () => void;
    imageUrl: string;
  };
};

const TableLocations = memo(function TableLocations({ listLocations, selectedView, refetchLocation }: TTableLocation) {
  const { t } = useTranslation('locations');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [deleteLocation, { isLoading: isDeleteLocationLoading }] = useDeleteLocationMutation();
  const [duplicateDeliveryZone, { isLoading: isDuplicateDeliveryZoneLoading }] = useDuplicateDeliveryZoneMutation();
  const [deleteDeliveryZone, { isLoading: isDeleteDeliveryZoneLoading }] = useDeleteDeliveryZoneMutation();

  const [queryValue, setQueryValue] = useState<string>('');
  const [isFilter, setFilter] = useState<boolean>(false);
  const [typeDelete, setTypeDelete] = useState<TypeDelete>(TypeDelete.LOCATION);
  const [showModal, setShowModal] = useState({
    isShowModalUpgrade: false,
    isShowModalDelete: false,
    isShowModalDuplicate: false,
    isShowModalSortLocation: false,
  });
  const [deliveryId, setDeliveryId] = useState<{ selectedLocationId: number | null; duplicateZoneId: number | null }>({
    selectedLocationId: null,
    duplicateZoneId: null,
  });
  const [listLocationsFilter, setListLocationFilter] = useState<IListLocation[]>(listLocations);

  const textStatus = {
    textOn: t('tableLocation.enable'),
    textOff: t('tableLocation.disable'),
  };

  const resourceName = {
    singular: 'location',
    plural: 'location',
  };

  const handleShowDetailLocation = (id: number) => {
    navigate({ pathname: `${PATH.LOCATIONS.pathname}/${id}`, search: searchParams.toString() });
  };
  const handleEditPickupSettings = (id: number) => {
    navigate({ pathname: `${PATH.PICKUP.pathname}/${id}`, search: searchParams.toString() });
  };

  const handleShowModalDeleteLocation = (id: number) => {
    setShowModal({ ...showModal, isShowModalDelete: true });
    setDeliveryId((prev) => ({ ...prev, selectedLocationId: id }));
  };

  const handleShowModalDuplicateZone = (id: number) => {
    setDeliveryId((prev) => ({ ...prev, duplicateZoneId: id }));
    setShowModal({ ...showModal, isShowModalDuplicate: true });
  };

  const handleConfirmDelete = (id: number) => {
    if (selectedView === LocationViewChoices.ALL) {
      deleteLocation({ shop: config.shop, id })
        .unwrap()
        .then(() => {
          setShowModal({ ...showModal, isShowModalDelete: false });
          openToast(dispatch, 'Successfully deleted');
          refetchLocation();
        })
        .catch(() => openErrorToast(dispatch, 'Fail deleted'));
    } else {
      deleteDeliveryZone({ shop: config.shop, id })
        .unwrap()
        .then(() => {
          setShowModal({ ...showModal, isShowModalDelete: false });
          openToast(dispatch, 'Successfully deleted');
          refetchLocation();
        });
    }
  };

  const handleDuplicateZone = (id: number) => {
    duplicateDeliveryZone({ shop: config.shop, duplicateZoneId: id })
      .unwrap()
      .then(() => {
        openToast(dispatch, 'Successfully duplicated');
        setShowModal({ ...showModal, isShowModalDuplicate: false });
        refetchLocation();
      })
      .catch(() => openErrorToast(dispatch, 'Fail duplicated'));
  };

  const handleSelectedView = (index: number) => {
    setFilter(false);
    dispatch(handleLocationTab({ key: 'locationTab', value: index }));
  };

  const listFilterByView = () => {
    switch (selectedView) {
      case LocationViewChoices.PICKUP:
        return listLocationsFilter.filter((item) => item.isEnablePickup);
      case LocationViewChoices.DELIVERY:
        return listLocationsFilter.filter((item) => item.isEnableDelivery);
      default:
        return listLocationsFilter;
    }
  };

  const rowMarkup = listFilterByView()
    .sort((a, b) => a.position - b.position)
    .map((item, index) => (
      <RowTableLocations
        key={index + selectedView}
        item={item}
        index={index}
        handleShowDetailLocation={handleShowDetailLocation}
        selectedView={selectedView}
        textStatus={textStatus}
        handleShowModalDeleteLocation={handleShowModalDeleteLocation}
        handleEditPickupSettings={handleEditPickupSettings}
      />
    ));

  const handleChangeValueFilter = (value: string) => {
    setQueryValue(value);
    setFilter(!isEmpty(value.trim()));
    const listFilter = listLocations.filter((item) => {
      if (selectedView === LocationViewChoices.DELIVERY) {
        return (
          item.name.toLowerCase().includes(value.trim().toLowerCase()) ||
          item.address?.address1?.toLowerCase().includes(value.trim().toLowerCase()) ||
          item.settings
            .find((item) => item.type === SETTING_TYPE.DELIVERY)
            ?.name.toLowerCase()
            .includes(value.trim().toLowerCase())
        );
      }

      return (
        item.name.toLowerCase().includes(value.toLowerCase()) ||
        item.address?.address1?.toLowerCase().includes(value.toLowerCase())
      );
    });
    setListLocationFilter(listFilter);
  };

  const handleAddLocation = () => {
    navigate(`${PATH.LOCATIONS.pathname}/new`);
  };

  const handleEnable = () => {
    handleChangeValueFilter('');
    dispatch(handleLocationTab({ key: 'locationTab', value: LocationViewChoices.ALL }));
  };

  useEffect(() => {
    setListLocationFilter(listLocations);
  }, [listLocations]);

  const renderHeading: NonEmptyArray<IndexTableHeading> = useMemo(() => {
    switch (selectedView) {
      case LocationViewChoices.PICKUP:
        return [
          {
            title: <BoldSmallText>{t('tableLocation.pickupLocations')}</BoldSmallText>,
            id: `pickupLocations_${selectedView}`,
          },
          {
            title: <BoldSmallText>{t('tableLocation.status')}</BoldSmallText>,
            id: `status_${selectedView}`,
          },
          {
            title: <BoldSmallText>{t('tableLocation.action')}</BoldSmallText>,
            id: `action_${selectedView}`,
          },
        ];
      case LocationViewChoices.DELIVERY:
        return [
          {
            title: <BoldSmallText>{t('tableLocation.deliveryZone')}</BoldSmallText>,
            id: `deliveryZone_${selectedView}`,
          },
          {
            title: <BoldSmallText>{t('tableLocation.localDelivery')}</BoldSmallText>,
            id: `localDelivery_${selectedView}`,
          },
          { title: '' },
          {
            title: <BoldSmallText>{t('tableLocation.action')}</BoldSmallText>,
            id: `action_${selectedView}`,
          },
        ];
      default:
        return [
          {
            title: <BoldSmallText>{t('tableLocation.location')}</BoldSmallText>,
            id: `location_${selectedView}`,
          },
          {
            title: <BoldSmallText>{t('tableLocation.storePickup')}</BoldSmallText>,
            id: `storePickup_${selectedView}`,
          },
          {
            title: <BoldSmallText>{t('tableLocation.localDelivery')}</BoldSmallText>,
            id: `localDelivery_${selectedView}`,
          },
          {
            title: <BoldSmallText>{t('tableLocation.action')}</BoldSmallText>,
            id: `action_${selectedView}`,
          },
        ];
    }
  }, [selectedView, t]);

  const dataEmptyState: TEmptyState = {
    0: {
      heading: 'Manage your pickup and delivery locations here',
      textEmptyState: 'Add a location to start using delivery and pickup location.',
      contentAction: 'Add location',
      onAction: handleAddLocation,
      imageUrl: 'https://cdn.shopify.com/s/files/1/0722/6218/9374/files/online_shopping_delivery.png?v=1724916105',
    },
    1: {
      heading: 'No active pickup locations found',
      textEmptyState: 'Enable pickup locations now',
      contentAction: 'Enable pickup',
      onAction: handleEnable,
      imageUrl: 'https://cdn.shopify.com/s/files/1/0722/6218/9374/files/Shopping_basket_empty.png?v=1724916147',
    },
    2: {
      heading: 'No active delivery locations found',
      textEmptyState: 'Enable delivery locations now',
      contentAction: 'Enable delivery',
      onAction: handleEnable,
      imageUrl:
        'https://cdn.shopify.com/s/files/1/0722/6218/9374/files/online_shopping_and_delivery_package_boxes.png?v=1724916191',
    },
  };

  return (
    <div className="dd-table-locations">
      <Card padding={'0'}>
        <BlockStack>
          {listLocations.length > 0 ? (
            <>
              <FilterTableLocations
                queryValue={queryValue}
                setQueryValue={setQueryValue}
                selectedView={selectedView}
                handleSelectedView={handleSelectedView}
                handleChangeValueFilter={handleChangeValueFilter}
                onSortColumn={() => setShowModal({ ...showModal, isShowModalSortLocation: true })}
                listLocations={listLocationsFilter}
              />
              <IndexTable
                resourceName={resourceName}
                itemCount={(listFilterByView() && listFilterByView()?.length) ?? 0}
                emptyState={!isFilter ? <EmptyStatePage {...dataEmptyState[selectedView]} /> : undefined}
                selectable={false}
                headings={renderHeading}
              >
                {selectedView === LocationViewChoices.DELIVERY ? (
                  <TableDelivery
                    listLocations={listFilterByView().sort((a, b) => a.position - b.position)}
                    setTypeDelete={setTypeDelete}
                    selectedView={selectedView}
                    handleShowModalDeleteLocation={handleShowModalDeleteLocation}
                    handleShowModalDuplicateZone={handleShowModalDuplicateZone}
                  />
                ) : (
                  rowMarkup
                )}
              </IndexTable>
            </>
          ) : (
            <EmptyStatePage
              heading={dataEmptyState[0].heading}
              textEmptyState={dataEmptyState[0].textEmptyState}
              contentAction={dataEmptyState[0].contentAction}
              onAction={dataEmptyState[0].onAction}
              imageUrl={dataEmptyState[0].imageUrl}
            />
          )}
        </BlockStack>
      </Card>
      <>
        <Suspense fallback={null}>
          <ModalUpgradePlan
            isShowModal={showModal.isShowModalUpgrade}
            onHide={() => setShowModal({ ...showModal, isShowModalUpgrade: false })}
            checkPlanAddMoreLocations={true}
          />
        </Suspense>
        <Suspense fallback={null}>
          <ModalDeleteLocations
            isShowModal={showModal.isShowModalDelete}
            typeDelete={typeDelete}
            selectedLocationId={deliveryId.selectedLocationId ?? 0}
            handleConfirmDelete={handleConfirmDelete}
            onCancel={() => setShowModal({ ...showModal, isShowModalDelete: false })}
            loading={isDeleteLocationLoading || isDeleteDeliveryZoneLoading}
          />
        </Suspense>
        <Suspense fallback={null}>
          <ModalDuplicateLocation
            isShowModal={showModal.isShowModalDuplicate}
            onAction={() => handleDuplicateZone(Number(deliveryId.duplicateZoneId))}
            onCancel={() => setShowModal({ ...showModal, isShowModalDuplicate: false })}
            loading={isDuplicateDeliveryZoneLoading}
          />
        </Suspense>
        <ModalSortLocation
          open={showModal.isShowModalSortLocation}
          onCancel={() => {
            setShowModal({ ...showModal, isShowModalSortLocation: false });
          }}
          listLocations={listLocationsFilter}
        />
      </>
    </div>
  );
});

export default TableLocations;
